/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
import { PureComponent } from 'react';

import { Moment } from 'moment';

/**
 * Calendar component
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.4.0
 * @since 1.0.1
 */
import CalendarBody, { IRangeDatePickerCalendarBody } from './CalendarBody';
import CalendarHead, { IRangeDatePickerCalendarHeader } from './CalendarHead';
import { CalendarTable } from './CalendarTable';
import {
    CalendarTableStyled,
    DateRangePickerStyled,
    DrpCalendarLeftStyled,
    DrpCalendarRightStyled,
    DrpCalendarStyled,
    VerticalSeparateLineStyled,
} from './styled/UnlinkedCalendarStyled';
import { TimePicker } from './TimePicker';

/**
 * IUnlinkedCalendar interface extends IRangeDatePickerCalendarBody & IRangeDatePickerCalendarHeader.
 *
 * opens: open direction.
 * position: calendar position.
 */
interface IUnlinkedCalendar extends IRangeDatePickerCalendarBody, IRangeDatePickerCalendarHeader {
    opens: any;
    position?: string;
    timeLeftHour: number;
    timeLeftMin: number;
    timePicker?: boolean;
    timeRightHour: number;
    timeRightMin: number;
    single?: boolean;
}

/**
 * UnlinkedCalendar component.
 *
 * props: IUnlinkedCalendar.
 * Render left and right calendar table.
 */
export class UnlinkedCalendar extends PureComponent<IUnlinkedCalendar> {

    static defaultProps = {
        position: 'left',
    };

    state = {
        leftCalendar: this.props.startDate,
        rightCalendar: this.props.endDate,
        timeLeftHour: this.props.timeLeftHour,
        timeLeftMin: this.props.timeLeftMin,
        timeRightHour: this.props.timeRightHour,
        timeRightMin: this.props.timeRightMin,
        single: this.props.single,
    };

    componentDidUpdate(prevProps: IUnlinkedCalendar) {
        if (prevProps.endDate !== this.props.endDate || prevProps.startDate !== this.props.startDate) {
            this.handleNext(this.props.endDate);
            this.handlePrev(this.props.startDate);
        }
    }

    /**
     * CreateProps - set props to left and right table.
     *
     * @return props for left and right calendar.
     */
    createProps = () => {
        const { leftCalendar, rightCalendar } = this.state;
        const leftState = Object.assign({}, this.props,
            { calendar: leftCalendar }, { side: 'left' });
        const rightState = Object.assign({}, this.props,
            { calendar: rightCalendar }, { side: 'right' });
        const {
            handlePrev,
            handleNext,
        } = this;
        return {
            leftProps: {
                ...leftState,
                handleSelected: handlePrev,
            },
            rightProps: {
                ...rightState,
                handleSelected: handleNext,
            },
        };
    };

    /**
     * handlePrev - change state of left side.
     *
     * @param leftCalendar: moment.Moment
     */
    handlePrev = (leftCalendar: Moment) => {
        this.setState({
            leftCalendar,
        });
    };

    /**
     * handleNext - change state of right side.
     *
     * @param rightCalendar: moment.Moment
     */
    handleNext = (rightCalendar: Moment) => {
        this.setState({
            rightCalendar,
        });
    };

    /**
     * renderTable - render whole table with two calendars and separateLine.
     */
    renderTable = () => {
        const { leftProps, rightProps } = this.createProps();
        // check state is correct
        if (this.isStartDateEqualToEnd() && (this.getMinRightHour() > this.state.timeRightHour)) {
            this.setState({
                timeRightHour: this.state.timeLeftHour,
            });
        }

        if (this.isStartDateHourEqualToEnd() && (this.getMinRightMin() > this.state.timeRightMin)) {
            if (this.state.timeLeftMin === 55) {
                this.setState({
                    timeRightHour: this.state.timeRightHour + 1,
                    timeRightMin: 0,
                });
            } else {
                this.setState({
                    timeRightMin: this.state.timeLeftMin + 5,
                });
            }
        }
        rightProps.leftDate = this.props.startDate;
        if (this.props.single) {
            return [
                <DrpCalendarLeftStyled
                    data-drp-calendar-left='calendarLeft'
                    key={0}>
                    <DrpCalendarStyled>
                        <CalendarTableStyled className="single-date-picker">
                            <CalendarTable className="range-date-picker__calendar table-condensed">
                                <CalendarHead {...leftProps} />
                                <CalendarBody {...leftProps} />
                            </CalendarTable>
                        </CalendarTableStyled>
                    </DrpCalendarStyled>

                    {
                        this.props.timePicker &&
                        <TimePicker
                            hour={this.state.timeLeftHour}
                            min={this.state.timeLeftMin}
                            onChangeHour={this.setLeftHour}
                            onChangeMin={this.setLeftMin}
                        />
                    }
                </DrpCalendarLeftStyled>,
            ];
        }
        return [
            <DrpCalendarLeftStyled
                data-drp-calendar-left='calendarLeft'
                key={0}>
                <DrpCalendarStyled>
                    <CalendarTableStyled>
                        <CalendarTable className="range-date-picker__calendar table-condensed">
                            <CalendarHead {...leftProps} />
                            <CalendarBody {...leftProps} />
                        </CalendarTable>
                    </CalendarTableStyled>
                </DrpCalendarStyled>

                {
                    this.props.timePicker &&
                    <TimePicker
                        hour={this.state.timeLeftHour}
                        min={this.state.timeLeftMin}
                        onChangeHour={this.setLeftHour}
                        onChangeMin={this.setLeftMin}
                    />
                }
            </DrpCalendarLeftStyled>,

            <VerticalSeparateLineStyled key={1}/>,

            <DrpCalendarRightStyled
                data-drp-calendar-right='calendarRight'
                key={2}>
                <DrpCalendarStyled>
                    <CalendarTableStyled>
                        <CalendarTable className="range-date-picker__calendar table-condensed">
                            <CalendarHead {...rightProps} />
                            <CalendarBody {...rightProps} />
                        </CalendarTable>
                    </CalendarTableStyled>
                </DrpCalendarStyled>

                {
                    this.props.timePicker &&
                    <TimePicker
                        hour={this.state.timeRightHour}
                        min={this.state.timeRightMin}
                        onChangeHour={this.setRightHour}
                        onChangeMin={this.setRightMin}
                    />
                }
            </DrpCalendarRightStyled>,
        ];
    };

    render() {
        const { opens, children } = this.props;
        return (
            <DateRangePickerStyled className={`opens${opens}`} data-range-picker="rangeDatePicker">
                {this.renderTable()}
                {children}
            </DateRangePickerStyled>
        );
    }

    // utils functions to time range check

    getMinRightHour = (): number => this.isStartDateEqualToEnd() ? this.state.timeLeftHour : 0;

    getMinRightMin = (): number => this.isStartDateHourEqualToEnd() ? this.state.timeLeftMin + 5 : 0;

    setLeftHour = (value: number | Range) => this.setState({ timeLeftHour: value });

    setLeftMin = (value: number | Range) => this.setState({ timeLeftMin: value });

    setRightHour = (value: number | Range) =>
        (value >= this.getMinRightHour()) && this.setState({ timeRightHour: value });

    setRightMin = (value: number | Range) =>
        (value >= this.getMinRightMin()) && this.setState({ timeRightMin: value });

    isStartDateHourEqualToEnd = (): boolean =>
        this.isStartDateEqualToEnd() && (this.state.timeLeftHour === this.state.timeRightHour);

    isStartDateEqualToEnd = (): boolean => {
        const { leftProps, rightProps } = this.createProps();
        return leftProps.startDate.format('DD MM YYYY') === rightProps.endDate.format('DD MM YYYY');
    };

}
